"use client";

import Image from "next/image";
import React, { useEffect, useState, useRef, useMemo } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { cn } from "@/lib/utils";
import { useAPYContext, fillAPYRate } from "@/features/apy";
import styles from "./GettingStartedSection.module.css";
import { landingContent } from "@/data/landingContent";

// Decorative elements
import BeachTowel from "@/assets/images/decorative/objects/BeachTowel.webp";

// Bank/Partner logos
import ChaseLogo from "@/assets/images/logos/partners/chase.webp";
import BoaLogo from "@/assets/images/logos/partners/boa.webp";
import WellsFargoLogo from "@/assets/images/logos/partners/wellsfargo.webp";

// App screenshots for each step
import CreateAccountImage from "@/assets/images/new/get_started_in_minutes/create_account.webp";
import PlaidInstitutionsImage from "@/assets/images/new/get_started_in_minutes/plaid_institutions.webp";
import DepositImage from "@/assets/images/new/get_started_in_minutes/deposit.webp";
import StashPlaceImage from "@/assets/images/new/get_started_in_minutes/stash_place.webp";

const bankLogos = [ChaseLogo, BoaLogo, WellsFargoLogo];

// Lock icon for encryption badge
const LockIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

// Plaid icon SVG component
const PlaidIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 28 29" fill="currentColor" className={className} aria-label="Plaid">
    <path d="M25.7629 26.2628L28 17.5309L24.9691 14.5001L27.9999 11.4691L25.7628 2.73706L17.0309 0.5L14.0001 3.531L10.969 0.50014L2.23706 2.73734L0 11.4691L3.03128 14.4999L0.00014 17.531L2.2372 26.2629L10.9691 28.5L14.0001 25.469L17.031 28.4999L25.7629 26.2628ZM15.7321 23.7371L18.6186 20.8505L22.2912 24.5233L17.6956 25.7007L15.7321 23.7371ZM11.1136 9.88154L14.0003 6.99502L16.8868 9.8814L14.0001 12.7679L11.1136 9.88154ZM12.2682 14.5L9.38154 17.3865L6.49502 14.5L9.38154 11.6135L12.2682 14.5ZM18.6187 11.6133L21.5053 14.5L18.6186 17.3865L15.7321 14.5L18.6187 11.6133ZM16.8867 19.1186L14.0001 22.0051L11.1135 19.1185L14.0001 16.2319L16.8867 19.1186ZM10.3044 25.7007L5.70864 24.5233L9.38154 20.8504L12.2682 23.7371L10.3044 25.7007ZM4.76308 16.2319L7.6496 19.1185L3.9767 22.7914L2.7993 18.1957L4.76308 16.2319ZM3.9767 6.20836L7.64974 9.8814L4.76308 12.7681L2.7993 10.8041L3.9767 6.20836ZM12.2683 5.26294L9.38168 8.1496L5.70892 4.4767L10.3047 3.2993L12.2683 5.26294ZM17.6959 3.2993L22.2915 4.4767L18.6186 8.14946L15.7321 5.26294L17.6959 3.2993ZM23.2372 12.7681L20.3505 9.8814L24.0233 6.20878L25.2007 10.8046L23.2372 12.7681ZM24.0233 22.7914L20.3505 19.1186L23.2372 16.2321L25.2007 18.1957L24.0233 22.7914Z"/>
  </svg>
);

// Floating background decorative icons (Lucide icons - ISC license)
const FloatingIcons = {
  palmTree: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M13 8c0-2.76-2.46-5-5.5-5S2 5.24 2 8h2l1-1 1 1h4" />
      <path d="M13 7.14A5.82 5.82 0 0 1 16.5 6c3.04 0 5.5 2.24 5.5 5h-3l-1-1-1 1h-3" />
      <path d="M5.89 9.71c-2.15 2.15-2.3 5.47-.35 7.43l4.24-4.25.7-.7.71-.71 2.12-2.12c-1.95-1.96-5.27-1.8-7.42.35" />
      <path d="M11 15.5c.5 2.5-.17 4.5-1 6.5h4c2-5.5-.5-12-1-14" />
    </svg>
  ),
  wave: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 6c.6.5 1.2 1 2.5 1C7 7 7 5 9.5 5c2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" />
      <path d="M2 12c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" />
      <path d="M2 18c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" />
    </svg>
  ),
  cloud: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" />
    </svg>
  ),
  dollar: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" x2="12" y1="2" y2="22" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  ),
  sun: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2" />
      <path d="M12 20v2" />
      <path d="m4.93 4.93 1.41 1.41" />
      <path d="m17.66 17.66 1.41 1.41" />
      <path d="M2 12h2" />
      <path d="M20 12h2" />
      <path d="m6.34 17.66-1.41 1.41" />
      <path d="m19.07 4.93-1.41 1.41" />
    </svg>
  ),
};

// Icon colors - muted pastels
const iconColors = {
  palmTree: '#D8E8D8', // muted pastel green
  dollar: '#E8DFCA',   // muted pastel gold
  wave: '#D8E0E8',     // muted pastel blue
  sun: '#EDE8C8',      // muted pastel yellow
  cloud: '#EEEBE3',    // neutral (unchanged)
};

// Configuration for floating background icons - scattered across entire background
// Each icon has a parallax offset value for efficient transform calculation
const floatingIconsConfig = [
  // Top area
  { icon: 'palmTree', x: '8%', y: '8%', size: 72, delay: 0, rotate: -15, parallaxOffset: -480 },
  { icon: 'dollar', x: '35%', y: '5%', size: 42, delay: 0.3, rotate: 22, parallaxOffset: -320 },
  { icon: 'cloud', x: '65%', y: '10%', size: 68, delay: 0.7, rotate: -8, parallaxOffset: -360 },
  { icon: 'sun', x: '88%', y: '6%', size: 55, delay: 1.1, rotate: 12, parallaxOffset: -400 },
  // Upper middle
  { icon: 'wave', x: '18%', y: '28%', size: 65, delay: 1.4, rotate: -5, parallaxOffset: -450 },
  { icon: 'palmTree', x: '50%', y: '22%', size: 48, delay: 0.2, rotate: 18, parallaxOffset: -380 },
  { icon: 'dollar', x: '78%', y: '30%', size: 70, delay: 1.8, rotate: -25, parallaxOffset: -520 },
  // Middle area
  { icon: 'cloud', x: '5%', y: '48%', size: 52, delay: 0.5, rotate: 10, parallaxOffset: -390 },
  { icon: 'sun', x: '28%', y: '52%', size: 38, delay: 2.1, rotate: -18, parallaxOffset: -280 },
  { icon: 'wave', x: '72%', y: '45%', size: 70, delay: 0.9, rotate: 7, parallaxOffset: -340 },
  { icon: 'palmTree', x: '92%', y: '50%', size: 66, delay: 1.6, rotate: -12, parallaxOffset: -420 },
  // Lower middle
  { icon: 'dollar', x: '15%', y: '68%', size: 45, delay: 2.4, rotate: 28, parallaxOffset: -350 },
  { icon: 'cloud', x: '42%', y: '72%', size: 72, delay: 1.2, rotate: -6, parallaxOffset: -490 },
  { icon: 'wave', x: '85%', y: '70%', size: 50, delay: 0.6, rotate: 15, parallaxOffset: -380 },
  // Bottom area
  { icon: 'sun', x: '6%', y: '88%', size: 60, delay: 1.9, rotate: -22, parallaxOffset: -330 },
  { icon: 'dollar', x: '32%', y: '92%', size: 72, delay: 2.7, rotate: 8, parallaxOffset: -400 },
  { icon: 'palmTree', x: '58%', y: '85%', size: 55, delay: 0.4, rotate: -20, parallaxOffset: -360 },
  { icon: 'cloud', x: '90%', y: '90%', size: 40, delay: 2.2, rotate: 25, parallaxOffset: -370 },
  // Below viewport - these get pulled up into view during scroll
  { icon: 'palmTree', x: '12%', y: '115%', size: 70, delay: 0.8, rotate: 14, parallaxOffset: -550 },
  { icon: 'wave', x: '45%', y: '110%', size: 46, delay: 1.5, rotate: -10, parallaxOffset: -480 },
  { icon: 'dollar', x: '75%', y: '120%', size: 68, delay: 0.3, rotate: 20, parallaxOffset: -600 },
  { icon: 'cloud', x: '25%', y: '130%', size: 58, delay: 2.0, rotate: -14, parallaxOffset: -680 },
  { icon: 'sun', x: '60%', y: '125%', size: 72, delay: 1.0, rotate: 6, parallaxOffset: -640 },
  { icon: 'wave', x: '88%', y: '118%', size: 54, delay: 1.7, rotate: -28, parallaxOffset: -570 },
  { icon: 'palmTree', x: '38%', y: '140%', size: 68, delay: 0.5, rotate: 16, parallaxOffset: -750 },
  { icon: 'dollar', x: '82%', y: '135%', size: 44, delay: 2.3, rotate: -9, parallaxOffset: -710 },
];

// Step icons as simple SVG components
const StepIcons = {
  account: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  bank: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 21h18" />
      <path d="M3 10h18" />
      <path d="M5 6l7-3 7 3" />
      <path d="M4 10v11" />
      <path d="M20 10v11" />
      <path d="M8 14v3" />
      <path d="M12 14v3" />
      <path d="M16 14v3" />
    </svg>
  ),
  deposit: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="15" />
      <polyline points="5 8 12 15 19 8" />
      <path d="M5 21h14" />
    </svg>
  ),
  stash: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
      <line x1="7" y1="7" x2="7.01" y2="7" />
    </svg>
  ),
};

export default function GettingStartedSection() {
  const { apyRate } = useAPYContext();
  const [activeStep, setActiveStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isScrollingToStep, setIsScrollingToStep] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);

  const { gettingStarted } = landingContent;

  // Map step icons and images
  const stepIcons = [StepIcons.account, StepIcons.bank, StepIcons.deposit, StepIcons.stash];
  const stepImages = [CreateAccountImage, PlaidInstitutionsImage, DepositImage, StashPlaceImage];

  // Build steps from content, replacing APY placeholders
  const steps = useMemo(() => gettingStarted.steps.map((step, index) => ({
    id: index,
    title: step.title,
    description: fillAPYRate(step.description, apyRate),
    icon: stepIcons[index],
    image: stepImages[index],
    badge: step.badge,
    showBankLogos: index === 1, // Bank linking step shows bank logos
  })), [apyRate, gettingStarted.steps]);

  // Scroll-based step progression (desktop)
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  // Transform scroll progress to step index (reaches last step at 88%, leaving 12% for "stick")
  const scrollBasedStep = useTransform(scrollYProgress, [0, 0.88], [0, steps.length - 1]);

  // Progress segments - one between each pair of circles
  // Each segment fills during its corresponding step transition
  const stepThreshold = 0.88 / (steps.length - 1); // ~0.293 per step

  // Segment 1: fills during step 0→1 transition
  const segment1Progress = useTransform(scrollYProgress, [0, stepThreshold], ["0%", "100%"]);
  // Segment 2: fills during step 1→2 transition
  const segment2Progress = useTransform(scrollYProgress, [stepThreshold, stepThreshold * 2], ["0%", "100%"]);
  // Segment 3: fills during step 2→3 transition
  const segment3Progress = useTransform(scrollYProgress, [stepThreshold * 2, stepThreshold * 3], ["0%", "100%"]);

  // Parallax transforms for floating icons (26 total: 18 visible + 8 below viewport)
  const p0 = useTransform(scrollYProgress, [0, 1], [0, floatingIconsConfig[0].parallaxOffset]);
  const p1 = useTransform(scrollYProgress, [0, 1], [0, floatingIconsConfig[1].parallaxOffset]);
  const p2 = useTransform(scrollYProgress, [0, 1], [0, floatingIconsConfig[2].parallaxOffset]);
  const p3 = useTransform(scrollYProgress, [0, 1], [0, floatingIconsConfig[3].parallaxOffset]);
  const p4 = useTransform(scrollYProgress, [0, 1], [0, floatingIconsConfig[4].parallaxOffset]);
  const p5 = useTransform(scrollYProgress, [0, 1], [0, floatingIconsConfig[5].parallaxOffset]);
  const p6 = useTransform(scrollYProgress, [0, 1], [0, floatingIconsConfig[6].parallaxOffset]);
  const p7 = useTransform(scrollYProgress, [0, 1], [0, floatingIconsConfig[7].parallaxOffset]);
  const p8 = useTransform(scrollYProgress, [0, 1], [0, floatingIconsConfig[8].parallaxOffset]);
  const p9 = useTransform(scrollYProgress, [0, 1], [0, floatingIconsConfig[9].parallaxOffset]);
  const p10 = useTransform(scrollYProgress, [0, 1], [0, floatingIconsConfig[10].parallaxOffset]);
  const p11 = useTransform(scrollYProgress, [0, 1], [0, floatingIconsConfig[11].parallaxOffset]);
  const p12 = useTransform(scrollYProgress, [0, 1], [0, floatingIconsConfig[12].parallaxOffset]);
  const p13 = useTransform(scrollYProgress, [0, 1], [0, floatingIconsConfig[13].parallaxOffset]);
  const p14 = useTransform(scrollYProgress, [0, 1], [0, floatingIconsConfig[14].parallaxOffset]);
  const p15 = useTransform(scrollYProgress, [0, 1], [0, floatingIconsConfig[15].parallaxOffset]);
  const p16 = useTransform(scrollYProgress, [0, 1], [0, floatingIconsConfig[16].parallaxOffset]);
  const p17 = useTransform(scrollYProgress, [0, 1], [0, floatingIconsConfig[17].parallaxOffset]);
  // Below viewport icons
  const p18 = useTransform(scrollYProgress, [0, 1], [0, floatingIconsConfig[18].parallaxOffset]);
  const p19 = useTransform(scrollYProgress, [0, 1], [0, floatingIconsConfig[19].parallaxOffset]);
  const p20 = useTransform(scrollYProgress, [0, 1], [0, floatingIconsConfig[20].parallaxOffset]);
  const p21 = useTransform(scrollYProgress, [0, 1], [0, floatingIconsConfig[21].parallaxOffset]);
  const p22 = useTransform(scrollYProgress, [0, 1], [0, floatingIconsConfig[22].parallaxOffset]);
  const p23 = useTransform(scrollYProgress, [0, 1], [0, floatingIconsConfig[23].parallaxOffset]);
  const p24 = useTransform(scrollYProgress, [0, 1], [0, floatingIconsConfig[24].parallaxOffset]);
  const p25 = useTransform(scrollYProgress, [0, 1], [0, floatingIconsConfig[25].parallaxOffset]);
  const parallaxTransforms = [p0, p1, p2, p3, p4, p5, p6, p7, p8, p9, p10, p11, p12, p13, p14, p15, p16, p17, p18, p19, p20, p21, p22, p23, p24, p25];

  // Update active step based on scroll (desktop only)
  useEffect(() => {
    if (isMobile || isScrollingToStep) return;

    const unsubscribe = scrollBasedStep.on("change", (latest) => {
      // Use floor so step only advances when progress bar is 100%
      const newStep = Math.min(Math.floor(latest + 0.01), steps.length - 1);
      if (newStep !== activeStep && newStep >= 0 && newStep < steps.length) {
        setActiveStep(newStep);
      }
    });

    return () => unsubscribe();
  }, [scrollBasedStep, activeStep, steps.length, isMobile, isScrollingToStep]);

  // Handle step click - scroll to corresponding position
  const handleStepClick = (stepIndex: number) => {
    if (stepIndex === activeStep) return;

    // Set the step immediately
    setActiveStep(stepIndex);

    if (!isMobile && containerRef.current) {
      // Block scroll-based updates during animation
      setIsScrollingToStep(true);

      const container = containerRef.current;
      const containerTop = container.offsetTop;
      const containerHeight = container.offsetHeight - window.innerHeight;

      // Calculate scroll position for this step
      const scrollProgress = stepIndex / (steps.length - 1);
      const targetScroll = containerTop + (containerHeight * scrollProgress);

      window.scrollTo({
        top: targetScroll,
        behavior: 'smooth'
      });

      // Re-enable scroll-based updates after animation completes
      setTimeout(() => {
        setIsScrollingToStep(false);
      }, 600);
    }
  };

  // Check for mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 900);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Intersection observer - detect when section is in view (for animations)
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isMobile]); // Re-attach observer when mobile state changes (different DOM element)

  // Mobile: auto-advance steps (resets on manual step change)
  useEffect(() => {
    if (!isMobile || !isVisible) return;

    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % steps.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isMobile, isVisible, steps.length, activeStep]);

  // Mobile swipe handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    const deltaY = e.changedTouches[0].clientY - touchStartY.current;

    // Only handle horizontal swipes (ignore vertical scrolling)
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
      if (deltaX < 0) {
        setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
      } else {
        setActiveStep((prev) => Math.max(prev - 1, 0));
      }
    }
  };

  const currentStep = steps[activeStep];

  // For mobile, render without scroll container
  if (isMobile) {
    return (
      <section
        ref={sectionRef}
        id="howitworkssection"
        className={styles.section}
      >
        {/* Header */}
        <header className={`${styles.header} ${isVisible ? styles.animateFadeIn : ""}`}>
          <div className={styles.decorationWrapper}>
            <Image
              src={BeachTowel}
              alt=""
              width={100}
              height={100}
              className={styles.decoration}
              aria-hidden="true"
            />
          </div>
          <h2 className={styles.headline}>{gettingStarted.headline}</h2>
          <p className={styles.subheadline}>
            {gettingStarted.subheadline}
          </p>
        </header>

        {/* Main Content */}
        <div
          className={styles.content}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {/* Step Details */}
          <div className={`${styles.stepDetails} ${isVisible ? styles.animateSlideInRight : ""}`}>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.4 }}
                className={styles.stepContent}
              >
                <div className={styles.stepIcon}>
                  {currentStep.icon}
                </div>
                <h3 className={styles.stepDetailTitle}>{currentStep.title}</h3>
                <p className={styles.stepDescription}>{currentStep.description}</p>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Trust badge - between content and phone on mobile */}
          <div className={styles.trustBadge}>
            {/* Non-animated background layer for images - prevents color flash during fade */}
            {currentStep.showBankLogos && (
              <div className={styles.trustBadgeBackgrounds} aria-hidden="true">
                <div className={styles.plaidLogoBg} />
                <span className={styles.bankDividerSpacer}>+</span>
                <div className={styles.bankLogosBg}>
                  <div className={styles.bankLogoBg} />
                  <div className={styles.bankLogoBg} />
                  <div className={styles.bankLogoBg} />
                  <span className={styles.moreTextSpacer}>& more</span>
                </div>
              </div>
            )}

            {/* Animated content layer */}
            <AnimatePresence mode="wait">
              <motion.div
                key={`badge-${activeStep}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className={styles.trustBadgeContent}
              >
                {currentStep.showBankLogos ? (
                  <div className={styles.bankLogosContainer}>
                    <PlaidIcon className={styles.plaidLogo} />
                    <span className={styles.bankDivider}>+</span>
                    <div className={styles.bankLogos}>
                      {bankLogos.map((logo, i) => (
                        <Image key={i} src={logo} alt="" width={32} height={32} className={styles.bankLogo} />
                      ))}
                      <span className={styles.moreText}>& more</span>
                    </div>
                  </div>
                ) : (
                  <span className={styles.badgeText}>
                    {currentStep.badge === "256-bit encryption" && (
                      <LockIcon className={styles.lockIcon} />
                    )}
                    {currentStep.badge}
                  </span>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Phone Display */}
          <div className={`${styles.phoneContainer} ${isVisible ? styles.animateScaleIn : ""}`}>
            <div className={styles.phoneFrame}>
              <div className={styles.phoneDynamicIsland} />
              <div className={styles.phoneScreen}>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeStep}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.05 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                    className={styles.phoneContent}
                  >
                    <Image
                      src={currentStep.image}
                      alt={`Step ${activeStep + 1}: ${currentStep.title}`}
                      className={styles.phoneScreenshot}
                      fill
                      sizes="(max-width: 768px) 280px, 320px"
                      priority={activeStep === 0}
                      placeholder="blur"
                    />
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Step Indicators */}
        <div className={styles.mobileIndicators}>
          {steps.map((_, index) => (
            <button
              key={index}
              onClick={() => handleStepClick(index)}
              className={cn(
                styles.indicator,
                activeStep === index && styles.indicatorActive
              )}
              aria-label={`Go to step ${index + 1}`}
            >
              {activeStep === index && (
                <motion.div
                  key={`progress-${activeStep}`}
                  className={styles.indicatorProgress}
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 5, ease: "linear" }}
                />
              )}
            </button>
          ))}
        </div>

        {/* CTA Button */}
        <div className={`${styles.ctaContainer} ${isVisible ? styles.animateFadeInUp : ""}`}>
          <a
            href="https://apps.apple.com/us/app/cove-your-money-haven/id1589645033"
            className={styles.ctaButton}
          >
            {gettingStarted.cta.text}
          </a>
          <p className={styles.ctaSubtext}>{gettingStarted.cta.subtext}</p>
        </div>
      </section>
    );
  }

  // Desktop: scroll-triggered sticky section
  return (
    <div ref={containerRef} className={styles.scrollContainer} id="howitworkssection">
      <section
        ref={sectionRef}
        className={styles.stickySection}
      >
        {/* Floating background icons */}
        <div className={styles.floatingIconsContainer} aria-hidden="true">
          {floatingIconsConfig.map((config, index) => (
            <motion.div
              key={index}
              className={styles.floatingIconWrapper}
              style={{
                left: config.x,
                top: config.y,
                y: parallaxTransforms[index],
              }}
            >
              <div
                className={styles.floatingIcon}
                style={{
                  width: config.size,
                  height: config.size,
                  animationDelay: `${config.delay}s`,
                  opacity: 0.65,
                }}
              >
                <div
                  style={{
                    transform: `rotate(${config.rotate}deg)`,
                    color: iconColors[config.icon as keyof typeof iconColors],
                  }}
                >
                  {FloatingIcons[config.icon as keyof typeof FloatingIcons]}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        {/* Header */}
        <header className={`${styles.header} ${isVisible ? styles.animateFadeIn : ""}`}>
          <div className={styles.decorationWrapper}>
            <Image
              src={BeachTowel}
              alt=""
              width={100}
              height={100}
              className={styles.decoration}
              aria-hidden="true"
            />
          </div>
          <h2 className={styles.headline}>{gettingStarted.headline}</h2>
          <p className={styles.subheadline}>
            {gettingStarted.subheadline}
          </p>
        </header>

        {/* Main Content */}
        <div className={styles.content}>
          {/* Step Navigation - Left Side */}
          <nav className={`${styles.stepNav} ${isVisible ? styles.animateSlideIn : ""}`}>
            {/* Progress column - circles interleaved with progress segments */}
            <div className={styles.progressColumn}>
              {steps.map((step, index) => {
                const segmentProgress = [segment1Progress, segment2Progress, segment3Progress][index];
                return (
                  <React.Fragment key={step.id}>
                    {/* Circle */}
                    <button
                      onClick={() => handleStepClick(index)}
                      className={styles.stepCircleButton}
                      aria-label={`Go to step ${index + 1}: ${step.title}`}
                    >
                      <div
                        className={cn(
                          styles.stepNumber,
                          activeStep === index && styles.stepNumberActive,
                          index < activeStep && styles.stepNumberComplete
                        )}
                        style={{
                          background: activeStep === index
                            ? 'linear-gradient(135deg, #6ACE57 0%, #07C8E6 100%)'
                            : index < activeStep
                              ? '#6ACE57'
                              : '#EBEBDF'
                        }}
                      >
                        {index < activeStep ? (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className={styles.checkIcon}>
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        ) : (
                          <span>{index + 1}</span>
                        )}
                      </div>
                    </button>
                    {/* Progress segment (not after last circle) */}
                    {index < steps.length - 1 && (
                      <div className={styles.progressSegment}>
                        <div className={styles.progressTrack} />
                        <motion.div
                          className={cn(
                            styles.progressFill,
                            index < activeStep && styles.progressFillComplete
                          )}
                          style={{ height: segmentProgress }}
                        />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {/* Title cards column */}
            <div className={styles.titleCardsColumn}>
              {steps.map((step, index) => (
                <button
                  key={`title-${step.id}`}
                  onClick={() => handleStepClick(index)}
                  className={cn(
                    styles.stepTitleCard,
                    activeStep === index && styles.stepTitleCardActive
                  )}
                  aria-current={activeStep === index ? "step" : undefined}
                >
                  <span className={cn(
                    styles.stepTitle,
                    activeStep === index && styles.stepTitleActive
                  )}>
                    {step.title}
                  </span>
                </button>
              ))}
            </div>
          </nav>

          {/* Phone Display - Center */}
          <div className={`${styles.phoneContainer} ${isVisible ? styles.animateScaleIn : ""}`}>
            <div className={styles.phoneFrame}>
              <div className={styles.phoneDynamicIsland} />
              <div className={styles.phoneScreen}>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeStep}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.05 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                    className={styles.phoneContent}
                  >
                    <Image
                      src={currentStep.image}
                      alt={`Step ${activeStep + 1}: ${currentStep.title}`}
                      className={styles.phoneScreenshot}
                      fill
                      sizes="(max-width: 768px) 280px, 320px"
                      priority={activeStep === 0}
                      placeholder="blur"
                    />
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>

            {/* Trust badge below phone - non-animated container for consistent background */}
            <div className={styles.trustBadge}>
              {/* Non-animated background layer for images - prevents color flash during fade */}
              {currentStep.showBankLogos && (
                <div className={styles.trustBadgeBackgrounds} aria-hidden="true">
                  <div className={styles.plaidLogoBg} />
                  <span className={styles.bankDividerSpacer}>+</span>
                  <div className={styles.bankLogosBg}>
                    <div className={styles.bankLogoBg} />
                    <div className={styles.bankLogoBg} />
                    <div className={styles.bankLogoBg} />
                    <span className={styles.moreTextSpacer}>& more</span>
                  </div>
                </div>
              )}

              {/* Animated content layer */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={`badge-${activeStep}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className={styles.trustBadgeContent}
                >
                  {currentStep.showBankLogos ? (
                    <div className={styles.bankLogosContainer}>
                      <PlaidIcon className={styles.plaidLogo} />
                      <span className={styles.bankDivider}>+</span>
                      <div className={styles.bankLogos}>
                        {bankLogos.map((logo, i) => (
                          <Image key={i} src={logo} alt="" width={32} height={32} className={styles.bankLogo} />
                        ))}
                        <span className={styles.moreText}>& more</span>
                      </div>
                    </div>
                  ) : (
                    <span className={styles.badgeText}>
                      {currentStep.badge === "256-bit encryption" && (
                        <LockIcon className={styles.lockIcon} />
                      )}
                      {currentStep.badge}
                    </span>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Step Details - Right Side */}
          <div className={`${styles.stepDetails} ${isVisible ? styles.animateSlideInRight : ""}`}>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.4 }}
                className={styles.stepContent}
              >
                <div className={styles.stepIcon}>
                  {currentStep.icon}
                </div>
                <h3 className={styles.stepDetailTitle}>{currentStep.title}</h3>
                <p className={styles.stepDescription}>{currentStep.description}</p>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* CTA Button */}
        <div className={`${styles.ctaContainer} ${isVisible ? styles.animateFadeInUp : ""}`}>
          <a
            href="https://apps.apple.com/us/app/cove-your-money-haven/id1589645033"
            className={styles.ctaButton}
          >
            {gettingStarted.cta.text}
          </a>
          <p className={styles.ctaSubtext}>{gettingStarted.cta.subtext}</p>
        </div>

        {/* Scroll indicator */}
        {activeStep < steps.length - 1 && (
          <div className={styles.scrollIndicator}>
            <span className={styles.scrollText}>{gettingStarted.scrollIndicator}</span>
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              className={styles.scrollArrow}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </motion.div>
          </div>
        )}

      </section>
    </div>
  );
}
